import { AnalyticsService } from '@/shared/analytics/analytics-service';
import type {
  BusinessWithId,
  PaymentMethod,
  SaleItem,
  SalePaymentInput,
} from '@/shared/schema';
import {
  ApplyPaymentParamsSchema,
} from '@/shared/schema/finance/money-flow-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { APP_KEYS } from '../hooks/query-keys';
import { apiClient } from '@/backend/api/api-client';

export interface CreateSaleWithPaymentsParams {
  creatorId: string;
  businessId: string;
  clientId: string; // Required now as everything revolves around a captured lead
  clientName: string;
  items: SaleItem[];
  payments: (SalePaymentInput & { receivedById: string })[];
  tasa: number;
}

export interface ApplyPaymentParams {
  creatorId: string;
  businessId: string;
  clientId: string;
  amountUSD: number;
  paymentMethod: PaymentMethod;
  reference?: string;
  tasa: number;
}

export const MoneyFlowService = {
  /**
   * 🛒 APLICAR PAGO (A TRAVÉS DE SERVER BRIDGE)
   * Registra un cobro vinculado a una venta de producto para un Lead específico.
   * Esto actualizará el LTV del cliente y preparará el trigger de Meta Purchase.
   */
  applyPaymentDB: async (rawParams: {
    saleId?: string | null;
    creatorId: string;
    businessId: string;
    clientId: string; // The captured Lead/Client
    amountLocal?: number;
    tasa: number;
    paymentMethod: any;
    reference?: string;
    activeBusiness: BusinessWithId;
  }) => {
    // 1. Light Validation
    const validation = ApplyPaymentParamsSchema.safeParse({
      ...rawParams,
      professionalId: rawParams.businessId, 
      receivedById: rawParams.businessId,
      receiverType: 'business'
    });
    
    if (!validation.success) {
      const errorMsg = validation.error.issues.map(i => i.message).join(', ');
      throw new Error(`Datos inválidos: ${errorMsg}`);
    }
    const params = validation.data;

    try {
      // 2. Direct Dispatch to Server Bridge
      return await apiClient.post('/api/finance', {
        action: 'applyPayment',
        params: {
          saleId: params.saleId,
          creatorId: params.creatorId,
          businessId: params.businessId,
          professionalId: params.businessId, // Simplified for solo account
          clientId: rawParams.clientId,
          amountLocal: params.amountLocal,
          paymentMethod: params.paymentMethod,
          tasa: params.tasa,
          receivedById: params.businessId,
          receiverType: 'business',
          activeBusiness: params.activeBusiness,
        },
      });
    } catch (error: any) {
      AnalyticsService.trackError(error, 'MoneyFlowService.applyPaymentDB');
      throw error;
    }
  },

  /**
   * 📉 REGISTRO DE EGRESO
   */
  registerExpenseDB: async (params: any) => {
    try {
      return await apiClient.post('/api/finance', {
        action: 'registerExpense',
        params,
      });
    } catch (e: any) {
      AnalyticsService.trackError(e, 'MoneyFlowService.registerExpenseDB');
      throw e;
    }
  },

  // MUTATIONS (Query Hooks)
  applyPayment: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: MoneyFlowService.applyPaymentDB,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: APP_KEYS.finance.all });
        queryClient.invalidateQueries({ queryKey: APP_KEYS.sales.all });
        queryClient.invalidateQueries({ queryKey: APP_KEYS.clients.all });
        toast.success('Cobro registrado y venta actualizada.');
      },
    });
  },

  useExpenseMutation: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ['finance', 'expense'],
      mutationFn: MoneyFlowService.registerExpenseDB,
      onSuccess: (_, variables) => {
        const ownerId = variables.businessId;
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.finance.owner(ownerId!),
        });
        toast.success('Egreso guardado correctamente.');
      },
      onError: (err: any) => {
        toast.error('Error al guardar egreso: ' + err.message);
      },
    });
  },
};
